import React, { lazy } from 'react';
export interface BrandLockupProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}
/**
 * Source sticker logo (~2 MB PNG). It is rendered at most ~400px wide, so we
 * never want the raw file on screen — especially on mobile. The Magic Patterns
 * CDN supports on-the-fly resizing + WebP conversion via query params, so we
 * generate a `srcset` of small WebP candidates and let the browser pick the
 * one matching the rendered width × device-pixel-ratio. The untransformed PNG
 * stays as the `src` fallback if a transform is ever unavailable.
 */
const LOGO_BASE = 'https://cdn.magicpatterns.com/uploads/fTNd19GFxwFNn1AjFC5Y7n/Adobe_Express_-_file_(1).png';
/** Build a resized + WebP-encoded CDN URL for a target pixel width. */
function cdnVariant(width: number): string {
  return `${LOGO_BASE}?format=webp&quality=80&width=${width}`;
}
/** Per-size config: the CSS slot width(s) and the candidate widths to offer. */
const SIZE_CONFIG: Record<NonNullable<BrandLockupProps['size']>, {
  className: string;
  sizes: string;
  widths: number[];
}> = {
  // 120px slot → up to 3× DPR
  sm: {
    className: 'max-w-[120px]',
    sizes: '120px',
    widths: [120, 240, 360]
  },
  // 180px slot
  md: {
    className: 'max-w-[180px]',
    sizes: '180px',
    widths: [180, 360, 540]
  },
  // 240px slot
  lg: {
    className: 'max-w-[240px]',
    sizes: '240px',
    widths: [240, 480, 720]
  },
  // 320px on mobile, 400px from sm breakpoint up
  xl: {
    className: 'max-w-[320px] sm:max-w-[400px]',
    sizes: '(min-width: 640px) 400px, 320px',
    widths: [320, 400, 640, 800, 1200]
  }
};
/**
 * BrandLockup — Full sticker logo (Mascot + Wordmark).
 *
 * Used for hero moments and pre-auth entry screens.
 * Do not use in persistent app chrome (use DjayMeesterLogo instead).
 */
export function BrandLockup({
  size = 'md',
  className = ''
}: BrandLockupProps) {
  const config = SIZE_CONFIG[size];
  const srcSet = config.widths.map(w => `${cdnVariant(w)} ${w}w`).join(', ');
  return <div className={`relative flex items-center justify-center select-none ${config.className} ${className}`} data-id="element-2891">
      <img
    // Small WebP default for browsers without srcset; full PNG as last resort.
    src={cdnVariant(config.widths[config.widths.length - 1])} srcSet={srcSet} sizes={config.sizes} alt="Meester djay logo" className="w-full h-auto object-contain drop-shadow-sm" loading="lazy" decoding="async" draggable={false} data-id="element-2892" />
    </div>;
}